import React from 'react';
import { captureSentryException } from '../sentry';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * ErrorBoundary - Catches React errors and displays fallback UI
 *
 * Captures errors to Sentry with component stack traces
 * Provides user-friendly error UI with recovery options
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a harmless WalletConnect session cleanup error
    const errorMessage = error?.message || String(error) || '';

    if (errorMessage.includes('session topic') ||
        errorMessage.includes('No matching key') ||
        errorMessage.includes('proposal:')) {
      // This is a harmless WalletConnect cleanup error - suppress it
      return { hasError: false }; // Don't show error UI
    }

    // For all other errors, show the error UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a harmless WalletConnect session cleanup error
    const errorMessage = error?.message || String(error) || '';

    if (errorMessage.includes('session topic') ||
        errorMessage.includes('No matching key') ||
        errorMessage.includes('proposal:')) {
      // This is a harmless WalletConnect cleanup error - suppress it
      return; // Don't report to Sentry
    }

    // For all other errors, log error details to state
    this.setState({
      error,
      errorInfo
    });

    // Capture error to Sentry with component stack
    captureSentryException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    // Also log to console in development
    // if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught error:', error, errorInfo);
    // }
  }

  handleReset = () => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    // Reload the page
    window.location.reload();
  };

  handleGoHome = () => {
    // Navigate to home page
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-100 rounded-full">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Something Went Wrong
            </h1>

            {/* Error Description */}
            <p className="text-gray-600 text-center mb-8">
              We're sorry, but something unexpected happened. Our team has been notified
              and we're working to fix the issue.
            </p>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-8 p-4 bg-gray-100 rounded-lg border border-gray-300">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  Error Details (Development Only)
                </h3>
                <div className="text-xs text-gray-700 font-mono overflow-auto">
                  <p className="mb-2 font-bold text-red-600">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="whitespace-pre-wrap text-gray-600">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {/* Additional Help */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                If the problem persists, please contact support at{' '}
                <a
                  href="mailto:support@coincommons.io"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  support@coincommons.io
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
